import { useEffect, useRef, useState } from 'react';

import { factoryService } from '../../services/factoryService';

export type AuthImageLoadedMeta = {
  byteSize: number;
  contentType: string;
  width: number;
  height: number;
};

type Props = {
  url: string;
  alt?: string;
  className?: string;
  onError?: () => void;
  onLoad?: () => void;
  onLoadedMeta?: (meta: AuthImageLoadedMeta) => void;
};

/** <img> with Bearer auth: fetch → blob URL (AppFactory-proxied MinerU assets / crop). */
export function AuthImage({ url, alt = '', className, onError, onLoad, onLoadedMeta }: Props) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  const [failMessage, setFailMessage] = useState('');
  const blobMetaRef = useRef<{ byteSize: number; contentType: string } | null>(null);
  const onLoadedMetaRef = useRef(onLoadedMeta);
  onLoadedMetaRef.current = onLoadedMeta;

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    setFailed(false);
    setFailMessage('');
    setSrc('');
    blobMetaRef.current = null;

    (async () => {
      try {
        const token = await factoryService.getAccessToken();
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try {
            const body = await res.json() as { message?: string; error?: string };
            detail = String(body.message || body.error || detail);
          } catch {
            /* not json */
          }
          throw new Error(detail);
        }
        const blob = await res.blob();
        if (cancelled) return;
        if (!blob.size) throw new Error('页图为空（0 字节）');
        const headerType = String(res.headers.get('Content-Type') || '').trim();
        const contentType = headerType || blob.type || 'image/jpeg';
        if (contentType.includes('json') || contentType.includes('text')) {
          throw new Error('页图接口未返回图片');
        }
        blobMetaRef.current = {
          byteSize: blob.size,
          contentType,
        };
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch (err) {
        if (!cancelled) {
          setFailed(true);
          setFailMessage(err instanceof Error ? err.message : String(err));
          onError?.();
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, onError]);

  if (failed) {
    return (
      <div className={`auth-image-failed ${className || ''}`.trim()} role="alert">
        <div className="auth-image-failed-title">页图加载失败</div>
        <div className="auth-image-failed-detail">{failMessage || '未知错误'}</div>
      </div>
    );
  }

  if (!src) {
    return <div className={`auth-image-loading ${className || ''}`.trim()} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onLoad={(e) => {
        const img = e.currentTarget;
        const blob = blobMetaRef.current;
        if (blob) {
          onLoadedMetaRef.current?.({
            ...blob,
            width: img.naturalWidth || 0,
            height: img.naturalHeight || 0,
          });
        }
        onLoad?.();
      }}
      onError={() => {
        setFailed(true);
        setFailMessage('图片解码失败');
        onError?.();
      }}
    />
  );
}
